'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLightbulb } from '@fortawesome/pro-solid-svg-icons';
import { CircularProgress, Button } from 'src/components';
import { PageHeader } from 'src/components/Settings/components';
import {
  Tabs,
  TabSet,
  Tab,
  TabPanel,
  PulseButton,
} from 'src/components/PulseUI';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { useFilterSetWarning } from 'src/hooks/strategy/useFilterSetWarning';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { LOCATION } from 'src/hooks/filters/constants';
import {
  selectDynamicCriteria,
  clearDynamicCriteria,
} from 'src/redux/slices/aiListGeneratorSlice';
import {
  useCreateTargetStrategyMutation,
  useCreateEngageStrategyMutation,
  useUpdateTargetStrategyMutation,
  useUpdateEngageStrategyMutation,
} from 'src/api/redux';
import {
  FilterSetWarningModal,
  StrategyFloatingProgress,
  StrategyTypeButton,
  EngageConnectors,
  TargetConnectors,
  StrategyHistory,
} from './components';
import PatientJourneyConfiguration from './components/StrategyTypes/PatientJourneyConfiguration';
import {
  getStrategyConfig,
  getStrategyButtons,
  getStrategyLabel,
  getStrategyConfigComponent,
  validateStrategyProgress,
  validateStrategyComplete,
  StrategyValidationContext,
} from 'src/components/Strategy/config';
import { StrategyMetricEditing } from './components/StrategyMetricEditing';
import { BasicInformation } from './components/BasicInformation';
import { StrategyType } from 'src/redux/slices/strategyFormSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';

type StrategyMode = 'target' | 'engage';

interface AddEditStrategyProps {
  mode?: 'add' | 'edit' | 'copy';
  strategyId?: string;
  strategyMode?: StrategyMode;
}

const AddEditStrategy: React.FC<AddEditStrategyProps> = ({
  mode = 'add',
  strategyId,
  strategyMode: propStrategyMode,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();

  // Get strategy mode from query params or props
  const queryMode = searchParams.get('mode') as StrategyMode | undefined;
  const strategyMode = propStrategyMode || queryMode || 'target';
  const { setNotification } = useNotification();
  const log = useLogging();
  const { useCriteriaTemplates } = useFlags();
  const { closeAllButCurrent } = useDrawer();

  // RTK Query mutations
  const [createStrategy, { isLoading: isCreating }] =
    useCreateTargetStrategyMutation();
  const [createEngageStrategy, { isLoading: isCreatingEngage }] =
    useCreateEngageStrategyMutation();
  const [updateStrategy, { isLoading: isUpdating }] =
    useUpdateTargetStrategyMutation();
  const [updateEngageStrategy, { isLoading: isUpdatingEngage }] =
    useUpdateEngageStrategyMutation();

  const {
    formData,
    selectedType,
    freshFacesData,
    notReferralsData,
    patientJourneyData,
    hasUnsavedChanges,
    isEditMode,
    isCopyMode,
    dynamicCriteria,
    strategyDetails,
    isLoadingStrategyDetails,
    setStrategyType,
    setFormMode,
    loadStrategy,
    copyStrategy,
    markAsSaved,
    resetForm,
  } = useStrategyForm(strategyMode);

  // Handler for selecting a historical version
  const handleVersionSelect = useCallback(
    (historyEntry: any) => {
      // Clear dynamic criteria first to reset the hydration state
      dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));

      // Use requestAnimationFrame to allow React to process the clear
      requestAnimationFrame(() => {
        // Use loadStrategy to hydrate ALL fields (form data, criteria, type-specific data)
        loadStrategy(historyEntry);
      });

      log.event('Strategy history version selected', {
        source: 'AddEditStrategy',
        strategyId,
        historyId: historyEntry.history_id,
        isCurrent: historyEntry.is_current,
      });
    },
    [dispatch, loadStrategy, log, strategyId]
  );

  const isFreshFacesStrategy = selectedType === 'FRESH_FACES';

  // Get patient journey specific criteria from separate locations
  const indexEventCriteria = useSelector(
    selectDynamicCriteria(LOCATION.PATIENT_JOURNEY_INDEX_EVENT)
  );
  const targetEventCriteria = useSelector(
    selectDynamicCriteria(LOCATION.PATIENT_JOURNEY_TARGET_EVENT)
  );

  const [patientJourneyDirection, setPatientJourneyDirection] = React.useState<
    'BEFORE' | 'AFTER'
  >('AFTER');

  // State for patient journey inline validation errors
  const [patientJourneyErrors, setPatientJourneyErrors] = React.useState<{
    target_event_interval?: string;
  }>({});

  // State for filter set warning modal
  const [showFilterSetWarning, setShowFilterSetWarning] = useState(false);

  const isEngageStrategy = strategyMode === 'engage';

  // Get strategy config from registry
  const strategyConfig = getStrategyConfig(selectedType);

  // Get strategy buttons from registry
  const strategyButtons = useMemo(
    () => getStrategyButtons(strategyMode),
    [strategyMode]
  );

  // Build validation context for registry functions
  const validationContext: StrategyValidationContext = useMemo(
    () => ({
      formData,
      dynamicCriteria,
      freshFacesData,
      notReferralsData,
      patientJourneyData,
      indexEventCriteria,
      targetEventCriteria,
      strategyDetails,
      isEditMode,
      isCopyMode,
      isEngageStrategy,
      patientJourneyDirection,
    }),
    [
      formData,
      dynamicCriteria,
      freshFacesData,
      notReferralsData,
      patientJourneyData,
      indexEventCriteria,
      targetEventCriteria,
      strategyDetails,
      isEditMode,
      isCopyMode,
      isEngageStrategy,
      patientJourneyDirection,
    ]
  );

  // Check for problematic filter types in Fresh Faces filter set
  const filterSetIdForWarning =
    selectedType === 'FRESH_FACES' && freshFacesData?.filter_set
      ? typeof freshFacesData.filter_set === 'object'
        ? freshFacesData.filter_set.id
        : freshFacesData.filter_set
      : null;

  const { hasIgnoredFilters, ignoredFilterTypes } = useFilterSetWarning(
    filterSetIdForWarning
  );

  // Set form mode on mount
  useEffect(() => {
    setFormMode({
      isEditMode: mode === 'edit',
      isCopyMode: mode === 'copy',
      strategyId,
    });

    // Log view event
    let eventName = 'Add strategy page viewed';
    if (mode === 'edit') {
      eventName = 'Edit strategy page viewed';
    } else if (mode === 'copy') {
      eventName = 'Copy strategy page viewed';
    }

    log.event(eventName, {
      source: 'AddEditStrategy',
      mode,
      strategyId: strategyId || null,
    });
  }, [mode, strategyId, setFormMode, log]);

  // Clear form on unmount
  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  // Load existing strategy data
  useEffect(() => {
    if (!strategyDetails || !strategyId) return;

    if (mode === 'edit') {
      loadStrategy(strategyDetails);
    } else if (mode === 'copy') {
      copyStrategy(strategyDetails);
    }
  }, [mode, strategyId, strategyDetails, loadStrategy, copyStrategy]);

  // Initialize patient journey direction from loaded strategy
  useEffect(() => {
    if (strategyDetails && selectedType === 'PATIENT_JOURNEY') {
      const engageDetails = strategyDetails as import('src/api/redux/strategy/types').EngageStrategyDetail;
      const initialDirection =
        engageDetails.journey_definition?.delta?.mode ||
        engageDetails.target_event_direction ||
        'AFTER';

      const normalizeDirection = (value: any): 'BEFORE' | 'AFTER' => {
        switch (value) {
          case 1:
          case 'BEFORE':
            return 'BEFORE';
          case 2:
          case 'AFTER':
            return 'AFTER';
          default:
            return 'AFTER';
        }
      };

      setPatientJourneyDirection(normalizeDirection(initialDirection));
    }
  }, [strategyDetails, selectedType]);

  // Sync patient journey direction from Redux (when template is applied)
  // Normalizes numeric API values (1=BEFORE, 2=AFTER) to string equivalents
  useEffect(() => {
    if (selectedType === 'PATIENT_JOURNEY') {
      const raw = patientJourneyData?.target_event_direction as any;
      if (raw === 1 || raw === 'BEFORE') {
        setPatientJourneyDirection('BEFORE');
      } else if (raw === 2 || raw === 'AFTER') {
        setPatientJourneyDirection('AFTER');
      }
    }
  }, [selectedType, patientJourneyData?.target_event_direction]);

  const handleTypeSelection = (type: string) => {
    setStrategyType(type as StrategyType | null);
    log.event('Strategy type selected', {
      source: 'AddEditStrategy',
      strategyType: type,
      mode,
    });
  };

  const handleSubmit = async () => {
    if (!selectedType) {
      log.exception('Strategy type is required');
      setNotification({
        title: 'Strategy type required',
        message: 'Please select a strategy type before saving',
        type: 'error',
      });
      return;
    }

    // Check for Fresh Faces filter set with ignored filter types
    if (selectedType === 'FRESH_FACES' && hasIgnoredFilters) {
      setShowFilterSetWarning(true);
      return;
    }

    // Validate Patient Journey specific fields before submitting
    if (selectedType === 'PATIENT_JOURNEY') {
      const interval = patientJourneyData?.target_event_interval;
      const isIntervalValid = typeof interval === 'number' && interval >= 1;

      if (!isIntervalValid) {
        setPatientJourneyErrors({ target_event_interval: 'Please enter a valid time frame (minimum 1 month).' });
        document
          .querySelector('[data-testid="patient-journey-timing"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      setPatientJourneyErrors({});
    }

    await saveStrategy();
  };

  const saveStrategy = async () => {
    if (!strategyConfig) return;

    try {
      if (isEditMode && strategyId) {
        const updateData = strategyConfig.serializeForUpdate(validationContext);

        const strategyResult = isEngageStrategy
          ? await updateEngageStrategy({
              id: strategyId,
              ...updateData,
            }).unwrap()
          : await updateStrategy({
              id: strategyId,
              ...updateData,
            }).unwrap();

        log.event('Strategy updated', {
          source: 'AddEditStrategy',
          strategyId,
          strategyLabel: strategyResult.label,
          strategyType: selectedType,
        });

        setNotification({
          title: 'Strategy updated successfully',
          message: `Strategy ${strategyResult.label} updated successfully`,
          type: 'success',
        });

        markAsSaved();
        resetForm();
        router.push(
          `/settings/admin/strategy-manager/${
            isEngageStrategy ? 'engage' : 'target'
          }`
        );
      } else {
        const submitData = strategyConfig.serializeForCreate(validationContext);

        const result = isEngageStrategy
          ? await createEngageStrategy(submitData).unwrap()
          : await createStrategy(submitData).unwrap();

        log.event('Strategy created', {
          source: 'AddEditStrategy',
          strategyId: result.id,
          strategyLabel: result.label,
          strategyType: selectedType,
          strategyMode,
          mode: isCopyMode ? 'copy' : 'add',
        });

        setNotification({
          title: 'Strategy created successfully',
          message: `Strategy ${result.label} created successfully`,
          type: 'success',
        });

        resetForm();
        router.push(
          `/settings/admin/strategy-manager/${
            isEngageStrategy ? 'engage' : 'target'
          }`
        );
      }
    } catch (err: any) {
      const errorData = err?.data;
      const errorMessage =
        errorData?.error_message ||
        errorData?.detail ||
        (Array.isArray(errorData?.non_field_errors) ? errorData.non_field_errors[0] : null) ||
        err?.error_message ||
        'An error occurred. Please check all required fields and try again.';
      setNotification({
        title: 'Failed to save strategy',
        message: errorMessage,
        type: 'error',
      });
      log.exception(`Failed to save strategy: ${errorMessage}`);
    }
  };

  const handleCancel = () => {
    log.event('Strategy form cancelled', {
      source: 'AddEditStrategy',
      mode,
      strategyMode,
      hasUnsavedChanges: hasUnsavedChanges,
    });
    resetForm();
    router.push(
      `/settings/admin/strategy-manager/${
        isEngageStrategy ? 'engage' : 'target'
      }`
    );
  };

  // Unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Progress calculation using registry
  const calculateProgress = () => {
    let completed = 0;
    const total = 4;

    if (formData.label.trim()) completed++;
    if (formData.description.trim()) completed++;
    if (selectedType) completed++;

    // Use registry for type-specific validation
    if (validateStrategyProgress(selectedType, validationContext)) {
      completed++;
    }

    return Math.round((completed / total) * 100);
  };

  // Criteria validation using registry
  const areCriteriaComponentsConfigured = () => {
    return validateStrategyComplete(selectedType, validationContext);
  };

  // Render strategy configuration component from registry
  const renderStrategyTypeContent = () => {
    const ConfigComponent = getStrategyConfigComponent(selectedType);

    if (!ConfigComponent) {
      return (
        <div className="w-full h-24 flex items-center justify-center bg-neutral-100 border border-neutral-200 rounded-lg">
          {selectedType} has not been implemented.
        </div>
      );
    }

    // PatientJourneyConfiguration needs direction props and inline error support
    if (selectedType === 'PATIENT_JOURNEY') {
      return (
        <PatientJourneyConfiguration
          direction={patientJourneyDirection}
          onDirectionChange={setPatientJourneyDirection}
          errors={patientJourneyErrors}
          onClearError={() => setPatientJourneyErrors({})}
        />
      );
    }

    return <ConfigComponent />;
  };

  const showDisabledState = (status?: string) => {
    return !status || status === 'CREATED' || status === 'PROCESSING';
  };

  // Single unified form layout
  const strategyLabel = getStrategyLabel(selectedType);
  const isSaving =
    isCreating || isCreatingEngage || isUpdating || isUpdatingEngage;

  const handleOpenDrawer = () => {
    closeAllButCurrent('strategy-template-drawer');
  };

  return (
    <div className="relative flex flex-col gap-2">
      <PageHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleCancel}
              variant="plain"
              color="neutral"
              size="sm"
              circular
            >
              <Icon icon={faArrowLeft} className="w-4 h-4" />
            </Button>
            <div className="flex flex-col">
              <h1 className="text-base font-medium text-primary">
                {isEditMode
                  ? 'Edit Strategy'
                  : `Create New ${
                      isEngageStrategy ? 'Engage and Expand' : 'Target'
                    } Strategy`}
              </h1>
              <div className="text-xs text-tertiary leading-tight">
                {isEngageStrategy
                  ? 'Configure your engage/expand strategy to connect with providers based on their behaviors and patterns'
                  : 'Configure your targeting strategy to identify and prioritize prospects'}
              </div>
            </div>
          </div>
        </div>
      </PageHeader>

      <Tabs className="h-full flex flex-col">
        <div className="flex-none pb-3  px-4">
          <TabSet id="strategy-tabs" className="w-full">
            <Tab
              tabSetId="strategy-tabs"
              index={0}
              className={`${
                isEditMode ? 'w-full' : 'w-1/3'
              } flex items-center justify-center`}
            >
              Basic Information
            </Tab>
            {isEditMode && (
              <>
                <Tab
                  tabSetId="strategy-tabs"
                  index={1}
                  className="w-full flex items-center justify-center"
                >
                  Column Editing
                </Tab>
                <Tab
                  tabSetId="strategy-tabs"
                  index={2}
                  className="w-full flex items-center justify-center"
                >
                  Status
                </Tab>
              </>
            )}
          </TabSet>
        </div>

        <TabPanel
          tabSetId="strategy-tabs"
          className="flex flex-col flex-1"
          index={0}
        >
          <div className="mx-auto w-full">
            <div className="flex flex-col gap-4 bg-white rounded-xl border border-neutral-200 px-4 py-3">
              {/* Version History Bar - shown in edit mode */}
              <StrategyHistory
                strategyId={strategyId}
                strategyMode={strategyMode}
                onVersionSelect={handleVersionSelect}
              />

              <BasicInformation
                strategyMode={strategyMode}
                strategyType={selectedType}
              />

              <div className="flex flex-col gap-4">
                {!selectedType ? (
                  <>
                    <div className="flex flex-col gap-1">
                      <div className="text-lg font-medium text-primary leading-tight">
                        Strategy Type
                      </div>
                      <p className="text-tertiary text-xs leading-tight">
                        Choose the type of{' '}
                        {isEngageStrategy ? 'Engage/Expand' : 'Target'} strategy
                        you want to create
                      </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {strategyButtons.map((button) => (
                        <StrategyTypeButton
                          key={button.value}
                          onTypeSelect={handleTypeSelection}
                          strategyType={button.value}
                          icon={button.icon}
                          isSelected={selectedType === button.value}
                          disabled={button.disabled || isEditMode}
                          disabledReason={button.disabledReason}
                          description={button.description}
                        />
                      ))}
                    </div>
                    {isEditMode && (
                      <p className="text-xs text-neutral-500 mt-1">
                        Strategy type cannot be changed when editing existing
                        strategies.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <div
                          className="text-lg font-medium text-primary leading-tight"
                          data-testid={`${selectedType
                            ?.toLowerCase()
                            .replace(/_/g, '-')}-configuration-header`}
                        >
                          {`${strategyLabel} Configuration`}
                        </div>
                        <div className="text-tertiary text-xs leading-tight">
                          Configure your {strategyLabel} strategy criteria
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <PulseButton
                          variant="outline"
                          color="neutral"
                          size="sm"
                          onClick={() => setStrategyType(null)}
                          disabled={isEditMode || isCopyMode}
                          data-testid="change-type-button"
                          startDecorator={faArrowLeft}
                        >
                          Change Type
                        </PulseButton>
                        {useCriteriaTemplates &&
                          selectedType &&
                          !isFreshFacesStrategy && (
                            <PulseButton
                              variant="outline"
                              color="neutral"
                              size="sm"
                              onClick={handleOpenDrawer}
                              disabled={isEditMode || isCopyMode}
                              data-testid="use-template-button"
                              startDecorator={faLightbulb}
                            >
                              Use Template
                            </PulseButton>
                          )}
                      </div>
                    </div>
                    {renderStrategyTypeContent()}
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-2">
                {isEditMode && showDisabledState(strategyDetails?.status) && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <span className="font-medium">
                      Strategy must be in READY status to update. Current
                      status: {strategyDetails?.status}
                    </span>
                  </div>
                )}
                <div className="flex gap-4">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 border border-neutral-200 text-primary rounded-full hover:bg-neutral-100 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isSaving ||
                      (isEditMode &&
                        showDisabledState(strategyDetails?.status)) ||
                      (!isEditMode &&
                        !isCopyMode &&
                        (calculateProgress() < 100 ||
                          !areCriteriaComponentsConfigured())) ||
                      (isCopyMode && !areCriteriaComponentsConfigured())
                    }
                    className="px-4 py-2 bg-sky-600 text-white rounded-full hover:bg-sky-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <CircularProgress size="sm" color="neutral" />
                    ) : isEditMode ? (
                      'Update Strategy'
                    ) : (
                      'Save Strategy'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        {isEditMode && (
          <>
            <TabPanel
              tabSetId="strategy-tabs"
              className="flex flex-col w-full "
              index={1}
            >
              <StrategyMetricEditing
                strategyId={strategyId}
                strategyType={strategyMode}
              />
            </TabPanel>
            <TabPanel
              tabSetId="strategy-tabs"
              className="flex flex-col w-full "
              index={2}
            >
              {isEngageStrategy ? (
                <EngageConnectors strategyId={strategyId} />
              ) : (
                <TargetConnectors strategyId={strategyId} />
              )}
            </TabPanel>
          </>
        )}
      </Tabs>

      {!isEditMode && !isCopyMode && (
        <StrategyFloatingProgress
          progress={calculateProgress()}
          hasUnsavedChanges={hasUnsavedChanges}
          areCriteriaComponentsConfigured={areCriteriaComponentsConfigured()}
        />
      )}

      {isLoadingStrategyDetails && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}

      <FilterSetWarningModal
        isOpen={showFilterSetWarning}
        onClose={() => setShowFilterSetWarning(false)}
        onConfirm={() => {
          setShowFilterSetWarning(false);
          saveStrategy();
        }}
        filterTypes={ignoredFilterTypes}
      />
    </div>
  );
};

export default AddEditStrategy;
